// SPV Pool v1.0 — small presentational helpers shared across the
// capital-markets pages: status badges and KPI cards.

import type { ReactNode } from "react";
import { Badge } from "@/design-system/components/ui";
import type { BadgeProps } from "@/design-system/components/ui/Badge";
import type { SpvPoolStatus, SpvStatus } from "@/types/spvPool";
import type {
  InvestorCommitmentStatus,
  InvestorStatus,
} from "@/types/investors";
import { humanizeStatus } from "./format";

type BadgeStatus = NonNullable<BadgeProps["status"]>;

const POOL_STATUS_BADGE: Record<SpvPoolStatus, BadgeStatus> = {
  Planning: "draft",
  Building: "info",
  EligibilityReview: "review",
  IDUPDrafting: "review",
  InvestorMarketing: "info",
  InvestorCommitment: "gold",
  Closing: "gold",
  Active: "finalized",
  WoundDown: "archived",
};

const TRUST_STATUS_BADGE: Record<SpvStatus, BadgeStatus> = {
  Forming: "draft",
  Operating: "finalized",
  WindingDown: "review",
  Closed: "archived",
};

export function PoolStatusBadge({ status }: { status: SpvPoolStatus }) {
  return (
    <Badge status={POOL_STATUS_BADGE[status] ?? "draft"}>
      {humanizeStatus(status)}
    </Badge>
  );
}

export function TrustStatusBadge({ status }: { status: SpvStatus }) {
  return (
    <Badge status={TRUST_STATUS_BADGE[status] ?? "draft"}>
      {humanizeStatus(status)}
    </Badge>
  );
}

const INVESTOR_STATUS_BADGE: Record<InvestorStatus, BadgeStatus> = {
  Prospect: "draft",
  Active: "finalized",
  Restricted: "review",
  OffBoarded: "archived",
};

const COMMITMENT_STATUS_BADGE: Record<InvestorCommitmentStatus, BadgeStatus> = {
  Indication: "draft",
  Committed: "gold",
  Funded: "finalized",
  Withdrawn: "archived",
  Defaulted: "error",
};

export function InvestorStatusBadge({ status }: { status: InvestorStatus }) {
  return (
    <Badge status={INVESTOR_STATUS_BADGE[status] ?? "draft"}>
      {humanizeStatus(status)}
    </Badge>
  );
}

export function CommitmentStatusBadge({
  status,
}: {
  status: InvestorCommitmentStatus;
}) {
  return (
    <Badge status={COMMITMENT_STATUS_BADGE[status] ?? "draft"}>
      {humanizeStatus(status)}
    </Badge>
  );
}

/** Service-provider status — Active / Standby / Inactive across all types. */
export function ProviderStatusBadge({ status }: { status: string }) {
  const map: Record<string, BadgeStatus> = {
    Active: "finalized",
    Standby: "info",
    Inactive: "archived",
  };
  return <Badge status={map[status] ?? "draft"}>{humanizeStatus(status)}</Badge>;
}

/**
 * Verification chip used for accreditation / KYC columns. A verified-at date
 * means "Verified"; absence means "Pending".
 */
export function VerificationChip({
  verifiedAt,
  verifiedLabel = "Verified",
  pendingLabel = "Pending",
}: {
  verifiedAt: string | null | undefined;
  verifiedLabel?: string;
  pendingLabel?: string;
}) {
  return verifiedAt ? (
    <Badge status="finalized">{verifiedLabel}</Badge>
  ) : (
    <Badge status="review">{pendingLabel}</Badge>
  );
}

interface KpiCardProps {
  label: string;
  value: ReactNode;
  hint?: string;
}

export function KpiCard({ label, value, hint }: KpiCardProps) {
  return (
    <div className="bg-white-pure rounded-lg border border-border-light p-space-4 flex flex-col gap-space-1">
      <span className="text-label font-display uppercase tracking-label text-gray-mid">
        {label}
      </span>
      <span className="font-display font-bold text-h3 text-onyx">{value}</span>
      {hint && <span className="text-caption text-gray-mid">{hint}</span>}
    </div>
  );
}
